import { createClaimApi } from "@solomon-apps/shared/src/claims/ClaimApiClient";
import { ClaimPanel } from "@solomon-apps/shared/src/claims/ClaimPanel";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useMemo } from "react";

import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";
import {
    useClaimAccounts,
    useClaimDistributionSettings
} from "@/hooks/useClientClaims";
import { useClientContextKey } from "@/hooks/useClientContextKey";
import type { WalletView } from "@/services/clientWallets";
import { orgApiFetchResponse } from "@/services/orgApiFetch";

export function ClientClaims(): React.ReactElement {
    const context = useClientContextKey();
    return <ClientClaimsForContext key={context} context={context} />;
}
function ClientClaimsForContext({
    context
}: {
    context: string;
}): React.ReactElement {
    const { wallets, account, setSelected } = useClaimAccounts(context);
    return (
        <section
            className="py-6 border-b border-line-base"
            aria-label="Reward delivery"
        >
            <Text variant="body">Reward delivery</Text>
            {wallets.isPending && <Text variant="small">Loading wallets</Text>}
            {wallets.error && (
                <Text variant="small" role="alert">
                    {wallets.error.message}
                </Text>
            )}
            {!wallets.isPending && !wallets.error && !account && (
                <Text variant="small">
                    Register a wallet to manage reward delivery.
                </Text>
            )}
            <div
                className="flex flex-wrap gap-3 py-4"
                aria-label="Select reward wallet"
            >
                {wallets.data?.map((w) => (
                    <Button
                        key={w.uuid}
                        variant={
                            account?.uuid === w.uuid ? "sunken" : "transparent"
                        }
                        aria-pressed={account?.uuid === w.uuid}
                        onClick={() => setSelected(w.uuid)}
                    >
                        {w.label ??
                            `${w.address.slice(0, 6)}…${w.address.slice(-4)}`}
                    </Button>
                ))}
            </div>
            {account && (
                <WalletClaims
                    key={account.uuid}
                    account={account}
                    context={context}
                />
            )}
        </section>
    );
}
function WalletClaims({
    account,
    context
}: {
    account: WalletView;
    context: string;
}): React.ReactElement {
    const api = useMemo(
        () =>
            createClaimApi({
                audience: "org",
                account: account.uuid,
                scopeKey: `org:${CLIENT_OAUTH_CONFIG.apiBase}:${context}:${account.uuid}`,
                fetch: orgApiFetchResponse
            }),
        [account.uuid, context]
    );
    const { settings, mode } = useClaimDistributionSettings(
        context,
        account.uuid
    );
    return (
        <>
            <Text variant="small" className="break-all">
                {account.vaultAddress ?? account.address}
            </Text>
            <div
                className="flex flex-wrap gap-3 py-4"
                aria-label="Distribution method"
            >
                {(["stream", "claim"] as const).map((value) => (
                    <Button
                        key={value}
                        variant={
                            settings.data?.distributionMode === value
                                ? "sunken"
                                : "transparent"
                        }
                        aria-pressed={settings.data?.distributionMode === value}
                        disabled={
                            mode.isPending ||
                            settings.isPending ||
                            Boolean(settings.error)
                        }
                        onClick={() => mode.mutate(value)}
                    >
                        {value === "stream" ? "Stream" : "Claim"}
                    </Button>
                ))}
            </div>
            <Text variant="small">
                Switching to Stream automatically claims all pending rewards.
                Future rewards stream to this wallet.
            </Text>
            {(settings.error || mode.error) && (
                <Text variant="small" role="alert">
                    {(settings.error ?? mode.error)?.message}
                </Text>
            )}
            <ClaimPanel api={api} />
        </>
    );
}
